// Neo's status for the boat right now (TOM-512), read from the cloud's rules
// endpoint (TOM-509, cloud ADR 0334, api_contracts §5.20). Home shows it above
// the board.
//
// Re-read every 60 s, when the page comes back into view, and whenever
// `refreshKey` changes: the caller folds the boat's mode, its alarms and its
// anchor watch into the key, so a change to any of them re-reads. The cloud
// keeps a boat's inputs for 30 s, so a re-read can return the same
// `composedAt`.
//
// No two reads start closer than 10 s apart, 30 s after a failed one (TOM-894).
// The key changes several times while a page loads, and many times a second
// on a connection that keeps dropping; without the gap each change was a
// read, and the cloud's limit refused them. A burst is one read, at the gap's
// end. The iPhone app waits the same (NeoStatusStore, TOM-832).
//
// A body the shared decoder refuses (a mocked `{}`, a malformed answer) reads
// as no status, never as a crash. A failed read hides the block, unless a
// status was already read for this boat: that one stays, with its own time.

import { parseNeoStatus, type NeoStatus } from '@neomaris/protocol';
import { useEffect, useRef, useState } from 'react';

import { api } from '../api';

export const NEO_STATUS_REFRESH_MS = 60_000;
/** The least time between the starts of two reads. */
export const NEO_STATUS_MIN_GAP_MS = 10_000;
/** The least time after a failed read, a refusal for too many requests among them. */
export const NEO_STATUS_FAILURE_GAP_MS = 30_000;

/** When the last read for a boat started, and whether it failed. */
export interface NeoStatusLastRead {
  at: number;
  failed: boolean;
}

/** How long the next read waits: none before the first, else what is left of the gap. */
export function neoStatusWaitMs(last: NeoStatusLastRead | null, now: number): number {
  if (last === null) return 0;
  const gap = last.failed ? NEO_STATUS_FAILURE_GAP_MS : NEO_STATUS_MIN_GAP_MS;
  return Math.max(0, last.at + gap - now);
}

export type NeoStatusState =
  | { kind: 'loading' }
  | { kind: 'ready'; status: NeoStatus }
  | { kind: 'none' };

export function useNeoStatus(boatId: string, refreshKey: string): NeoStatusState {
  const [state, setState] = useState<NeoStatusState>({ kind: 'loading' });
  const seq = useRef(0);
  const last = useRef<{ boatId: string; status: NeoStatus } | null>(null);
  const lastRead = useRef<({ boatId: string } & NeoStatusLastRead) | null>(null);
  // The boat whose reads may still land. A new key keeps a read in flight (it
  // is this boat's, and the next read waits out the gap); another boat or an
  // unmount drops it.
  const landing = useRef<string | null>(null);

  useEffect(() => {
    landing.current = boatId;
    return () => {
      landing.current = null;
    };
  }, [boatId]);

  useEffect(() => {
    let timer: number | null = null;
    async function read(): Promise<void> {
      const mine = ++seq.current;
      const started = { boatId, at: Date.now(), failed: false };
      lastRead.current = started;
      try {
        const body = await api<unknown>(`/v1/boats/${encodeURIComponent(boatId)}/neo-status`);
        if (landing.current !== boatId || mine !== seq.current) return;
        const status = parseNeoStatus(body);
        if (status !== null) last.current = { boatId, status };
        setState(status === null ? { kind: 'none' } : { kind: 'ready', status });
      } catch {
        started.failed = true;
        if (landing.current !== boatId || mine !== seq.current) return;
        const kept = last.current?.boatId === boatId ? last.current.status : null;
        setState(kept === null ? { kind: 'none' } : { kind: 'ready', status: kept });
      }
    }
    // One read waits at a time: what asks while it waits is answered by it.
    function schedule(): void {
      if (timer !== null) return;
      const prior = lastRead.current?.boatId === boatId ? lastRead.current : null;
      timer = window.setTimeout(
        () => {
          timer = null;
          void read();
        },
        neoStatusWaitMs(prior, Date.now())
      );
    }
    schedule();
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') schedule();
    }, NEO_STATUS_REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') schedule();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [boatId, refreshKey]);

  // Another boat's status is never shown while this one's is read.
  useEffect(() => {
    if (last.current !== null && last.current.boatId !== boatId) {
      last.current = null;
      setState({ kind: 'loading' });
    }
  }, [boatId]);

  return state;
}

/** "8:42 PM" on the boat's clock, for the status's label row. */
export function composedTime(status: NeoStatus): string {
  const at = Date.parse(status.composedAt);
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: status.timeZone,
    }).format(at);
  } catch {
    // A zone this browser doesn't know: the browser's own clock.
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(at);
  }
}
